import { useState, useEffect, useCallback } from 'react'
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend,
} from 'recharts'
import { BarChart3, TrendingUp, Info, Save, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  Input,
  LoadingState,
  Select,
  Switch,
} from '@/components/ui'
import type { ColumnDef } from '@/components/ui'
import apiClient from '@/lib/api.ts'
import { getAiModels } from '@/api/aiAdapter.ts'
import { getTenants } from '@/api/tenantAdapter.ts'
import type { Tenant } from '@shared/types'

// ─── Types ───────────────────────────────────────────────────────────────────

type PeriodKey =
  | 'today'
  | 'this-week'
  | 'last-week'
  | 'week-before-last'
  | 'last-30-days'
  | 'this-month'
  | 'last-month'
  | 'month-before-last'
  | 'this-year'
  | 'last-year'
  | 'custom'

const PERIOD_LABELS: Record<PeriodKey, string> = {
  'today': 'Letzter Tag',
  'this-week': 'Aktuelle Woche',
  'last-week': 'Letzte Woche',
  'week-before-last': 'Vorletzte Woche',
  'last-30-days': 'Letzte 30 Tage',
  'this-month': 'Aktueller Monat',
  'last-month': 'Letzter Monat',
  'month-before-last': 'Vorletzter Monat',
  'this-year': 'Aktuelles Jahr',
  'last-year': 'Letztes Jahr',
  'custom': 'Benutzerdefiniert',
}

interface DateRange { from: string; to: string }

interface ModelPricing {
  provider: string
  modelId: string
  inputPriceUsdPer1M: number   // displayed as per-1M; stored backend-side as per-1k
  outputPriceUsdPer1M: number
}

interface AiPricingConfig {
  exchangeRateEurUsd: number
  models: ModelPricing[]
}

// ─── Actual backend response shapes (snake_case from SQL) ─────────────────────

interface ApiSummary {
  total_calls: number
  total_input_tokens: number
  total_output_tokens: number
  total_cost_eur: number
}

interface ApiTimeSeriesPoint {
  period: string            // YYYY-MM-DD (or week/month format depending on granularity)
  calls: number
  input_tokens: number
  output_tokens: number
  cost_eur: number
}

interface ApiModelRow {
  provider: string
  model: string
  calls: number
  input_tokens: number
  output_tokens: number
  cost_eur: number
}

interface ApiModuleRow {
  feature_module: string | null
  calls: number
  cost_eur: number
}

interface ApiTenantRow {
  tenant_id: string | null
  calls: number
  cost_eur: number
}

interface ApiAiStatistics {
  firstLogDate: string | null
  summary: ApiSummary
  timeSeries: ApiTimeSeriesPoint[]
  byProviderModel: ApiModelRow[]
  byFeatureModule: ApiModuleRow[]
  byTenant: ApiTenantRow[]
}

// ─── Pricing helpers ──────────────────────────────────────────────────────────

/** Parse flat system_settings response → structured frontend config.
 *  Backend stores USD per 1K tokens; frontend displays/receives per 1M. */
function parseFlatPricing(flat: Record<string, string>, flatModels: FlatModel[]): AiPricingConfig {
  const exchangeRateEurUsd = flat['ai.pricing.usd_eur_rate']
    ? (parseFloat(flat['ai.pricing.usd_eur_rate']) || 0.92)
    : 0.92

  const perThousand: Record<string, { input: number; output: number }> = {}
  for (const [key, val] of Object.entries(flat)) {
    const inputMatch = key.match(/^ai\.pricing\.(.+)\.input_per_1k$/)
    const outputMatch = key.match(/^ai\.pricing\.(.+)\.output_per_1k$/)
    if (inputMatch) {
      const id = inputMatch[1]!
      if (!perThousand[id]) perThousand[id] = { input: 0, output: 0 }
      perThousand[id]!.input = parseFloat(val) || 0
    } else if (outputMatch) {
      const id = outputMatch[1]!
      if (!perThousand[id]) perThousand[id] = { input: 0, output: 0 }
      perThousand[id]!.output = parseFloat(val) || 0
    }
  }

  // Resolve pricing for a model id using exact match first, then prefix match.
  // Backend uses short prefix keys (e.g. "claude-haiku") while model ids are full
  // versioned strings (e.g. "claude-haiku-3-5-20251001").
  function resolvePrice(modelId: string): { input: number; output: number } {
    if (perThousand[modelId]) return perThousand[modelId]!
    for (const key of Object.keys(perThousand)) {
      if (modelId.startsWith(key)) return perThousand[key]!
    }
    return { input: 0, output: 0 }
  }

  const models: ModelPricing[] = flatModels.map((m) => {
    const p = resolvePrice(m.id)
    return {
      provider: m.provider,
      modelId: m.id,
      inputPriceUsdPer1M: p.input * 1000,
      outputPriceUsdPer1M: p.output * 1000,
    }
  })

  return { exchangeRateEurUsd, models }
}

/** Convert structured frontend config → flat backend payload (per 1K). */
function toPricingPayload(config: AiPricingConfig) {
  const models: Record<string, { input_per_1k: number; output_per_1k: number }> = {}
  for (const m of config.models) {
    models[m.modelId] = {
      input_per_1k: m.inputPriceUsdPer1M / 1000,
      output_per_1k: m.outputPriceUsdPer1M / 1000,
    }
  }
  return { usd_eur_rate: config.exchangeRateEurUsd, models }
}

// ─── Date Range Helpers ───────────────────────────────────────────────────────

function toIso(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function getDateRange(period: PeriodKey): DateRange {
  const now = new Date()
  const today = toIso(now)

  switch (period) {
    case 'today': {
      return { from: today, to: today }
    }
    case 'this-week': {
      const d = new Date(now)
      d.setDate(d.getDate() - d.getDay() + (d.getDay() === 0 ? -6 : 1))
      return { from: toIso(d), to: today }
    }
    case 'last-week': {
      const d = new Date(now)
      const day = d.getDay() === 0 ? 7 : d.getDay()
      const monThis = new Date(d); monThis.setDate(d.getDate() - day + 1)
      const monLast = new Date(monThis); monLast.setDate(monThis.getDate() - 7)
      const sunLast = new Date(monThis); sunLast.setDate(monThis.getDate() - 1)
      return { from: toIso(monLast), to: toIso(sunLast) }
    }
    case 'week-before-last': {
      const d = new Date(now)
      const day = d.getDay() === 0 ? 7 : d.getDay()
      const monThis = new Date(d); monThis.setDate(d.getDate() - day + 1)
      const mon2 = new Date(monThis); mon2.setDate(monThis.getDate() - 14)
      const sun2 = new Date(monThis); sun2.setDate(monThis.getDate() - 8)
      return { from: toIso(mon2), to: toIso(sun2) }
    }
    case 'last-30-days': {
      const from = new Date(now)
      from.setDate(from.getDate() - 29)
      return { from: toIso(from), to: today }
    }
    case 'this-month': {
      const from = new Date(now.getFullYear(), now.getMonth(), 1)
      return { from: toIso(from), to: today }
    }
    case 'last-month': {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const to = new Date(now.getFullYear(), now.getMonth(), 0)
      return { from: toIso(from), to: toIso(to) }
    }
    case 'month-before-last': {
      const from = new Date(now.getFullYear(), now.getMonth() - 2, 1)
      const to = new Date(now.getFullYear(), now.getMonth() - 1, 0)
      return { from: toIso(from), to: toIso(to) }
    }
    case 'this-year': {
      return { from: `${now.getFullYear()}-01-01`, to: today }
    }
    case 'last-year': {
      const y = now.getFullYear() - 1
      return { from: `${y}-01-01`, to: `${y}-12-31` }
    }
    case 'custom':
    default:
      return { from: today, to: today }
  }
}

// ─── Formatters ───────────────────────────────────────────────────────────────

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)} K`
  return String(n)
}

function fmtEur(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 4 }).format(n)
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card>
      <Card.Body className="p-4">
        <p className="text-small text-gray-500 mb-1">{label}</p>
        <p className="text-xl font-semibold text-gray-900">{value}</p>
        {sub && <p className="text-caption text-gray-400 mt-0.5">{sub}</p>}
      </Card.Body>
    </Card>
  )
}

// ─── Pricing Section ─────────────────────────────────────────────────────────

interface FlatModel { id: string; name: string; provider: string }

function PricingSection() {
  const [collapsed, setCollapsed] = useState(true)
  const [models, setModels] = useState<FlatModel[]>([])
  const [config, setConfig] = useState<AiPricingConfig>({ exchangeRateEurUsd: 0.92, models: [] })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [modelsError, setModelsError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      try {
        const [modelsRes, pricingRes] = await Promise.allSettled([
          getAiModels(),
          apiClient.get<Record<string, string>>('/ai/pricing'),
        ])

        if (!mounted) return

        let flatModels: FlatModel[] = []
        if (modelsRes.status === 'fulfilled') {
          flatModels = Object.entries(modelsRes.value.data).flatMap(([provider, models]) =>
            models.map((m) => ({ ...m, provider }))
          )
        } else {
          if (mounted) setModelsError('KI-Adapter-Modelle konnten nicht geladen werden.')
        }
        if (mounted) setModels(flatModels)

        if (pricingRes.status === 'fulfilled') {
          // Backend returns flat Record<string,string> — map to structured
          setConfig(parseFlatPricing(pricingRes.value.data, flatModels))
        } else {
          // Endpoint not responding — init with zeroes
          setConfig({
            exchangeRateEurUsd: 0.92,
            models: flatModels.map((m) => ({
              provider: m.provider,
              modelId: m.id,
              inputPriceUsdPer1M: 0,
              outputPriceUsdPer1M: 0,
            })),
          })
        }
      } catch (_err) {
        if (mounted) setSaveError('Preiskonfiguration konnte nicht geladen werden.')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    void load()
    return () => { mounted = false }
  }, [])

  const getModelPrice = useCallback(
    (modelId: string) => config.models.find((m) => m.modelId === modelId),
    [config.models],
  )

  const setModelPrice = (modelId: string, provider: string, field: 'inputPriceUsdPer1M' | 'outputPriceUsdPer1M', value: number) => {
    setConfig((prev) => {
      const existing = prev.models.find((m) => m.modelId === modelId)
      if (existing) {
        return { ...prev, models: prev.models.map((m) => m.modelId === modelId ? { ...m, [field]: value } : m) }
      }
      return {
        ...prev,
        models: [...prev.models, { provider, modelId, inputPriceUsdPer1M: 0, outputPriceUsdPer1M: 0, [field]: value }],
      }
    })
  }

  const save = async () => {
    if (models.length === 0) return
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    try {
      // Map structured → flat backend format before sending
      await apiClient.put('/ai/pricing', toPricingPayload(config))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      setSaveError('Preiskonfiguration konnte nicht gespeichert werden.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <LoadingState label="Preiskonfiguration wird geladen …" />

  // Group models by provider
  const byProvider = models.reduce<Record<string, FlatModel[]>>((acc, m) => {
    if (!acc[m.provider]) acc[m.provider] = []
    acc[m.provider]!.push(m)
    return acc
  }, {})

  return (
    <Card>
      <Card.Header
        className="flex items-center justify-between gap-4 cursor-pointer select-none"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div>
          <h3 className="text-body font-semibold text-gray-900 flex items-center gap-2">
            Preiskonfiguration
            {collapsed
              ? <ChevronDown className="w-4 h-4 text-gray-400" />
              : <ChevronUp className="w-4 h-4 text-gray-400" />}
          </h3>
          <p className="text-caption text-gray-500 mt-0.5">
            Aktuelle Preise je Modell (USD / 1 Mio. Token). Standardwerte sind vorausgefüllt — nur anpassen, wenn Anthropic die Preise ändert.
          </p>
        </div>
        {!collapsed && (
          <Button
            variant="primary"
            size="sm"
            leadingIcon={saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            onClick={(e) => { e.stopPropagation(); void save() }}
            disabled={saving || models.length === 0}
          >
            {saved ? 'Gespeichert' : 'Speichern'}
          </Button>
        )}
      </Card.Header>
      {collapsed ? null : <Card.Body className="space-y-6">
        {modelsError && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-caption text-amber-700">{modelsError}</div>
        )}
        {saveError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-caption text-red-700">{saveError}</div>
        )}

        <div className="max-w-xs">
          <Input
            type="number"
            label="Wechselkurs EUR/USD"
            value={String(config.exchangeRateEurUsd)}
            onChange={(e) => setConfig((prev) => ({ ...prev, exchangeRateEurUsd: parseFloat(e.target.value) || 0 }))}
            hint="Wie viel EUR entspricht 1 USD? Standard: 0,92"
            step="0.001"
          />
        </div>

        {Object.entries(byProvider).map(([provider, providerModels]) => (
          <div key={provider}>
            <p className="text-small font-medium text-gray-700 mb-1 capitalize">{provider}</p>
            <div className="space-y-0.5">
              <div className="flex items-center gap-3 text-caption text-gray-400 px-1 pb-0.5">
                <span className="flex-1 min-w-0">Modell</span>
                <span className="w-32 shrink-0 text-right">Input (USD/1M)</span>
                <span className="w-32 shrink-0 text-right">Output (USD/1M)</span>
              </div>
              {providerModels.map((m) => {
                const price = getModelPrice(m.id)
                return (
                  <div key={m.id} className="flex items-center gap-3 px-1 py-0.5 rounded hover:bg-gray-50">
                    <span className="flex-1 min-w-0 font-mono text-xs text-gray-700 truncate" title={m.id}>{m.id}</span>
                    <input
                      type="number"
                      className="w-32 shrink-0 border border-gray-300 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
                      value={price?.inputPriceUsdPer1M ?? 0}
                      min={0}
                      step="0.01"
                      onChange={(e) => setModelPrice(m.id, m.provider, 'inputPriceUsdPer1M', parseFloat(e.target.value) || 0)}
                    />
                    <input
                      type="number"
                      className="w-32 shrink-0 border border-gray-300 rounded px-2 py-1 text-xs text-right focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
                      value={price?.outputPriceUsdPer1M ?? 0}
                      min={0}
                      step="0.01"
                      onChange={(e) => setModelPrice(m.id, m.provider, 'outputPriceUsdPer1M', parseFloat(e.target.value) || 0)}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        ))}

        {models.length === 0 && (
          <p className="text-caption text-gray-400 italic">Keine Modelle gefunden. Bitte zuerst einen KI-Adapter konfigurieren.</p>
        )}
      </Card.Body>}
    </Card>
  )
}

// ─── Statistics Section ───────────────────────────────────────────────────────

const MODEL_COLUMNS: ColumnDef<ApiModelRow, unknown>[] = [
  { accessorKey: 'provider', header: 'Provider', size: 120 },
  { accessorKey: 'model', header: 'Modell' },
  {
    accessorKey: 'input_tokens',
    header: 'Input',
    cell: ({ row }) => fmtTokens(row.original.input_tokens),
    meta: { align: 'right' },
  },
  {
    accessorKey: 'output_tokens',
    header: 'Output',
    cell: ({ row }) => fmtTokens(row.original.output_tokens),
    meta: { align: 'right' },
  },
  {
    id: 'total_tokens',
    header: 'Gesamt',
    cell: ({ row }) => fmtTokens(row.original.input_tokens + row.original.output_tokens),
    meta: { align: 'right' },
  },
  {
    accessorKey: 'cost_eur',
    header: 'Kosten (€)',
    cell: ({ row }) => fmtEur(row.original.cost_eur),
    meta: { align: 'right' },
  },
]

const MODULE_COLUMNS: ColumnDef<ApiModuleRow, unknown>[] = [
  {
    accessorKey: 'feature_module',
    header: 'Feature-Modul',
    cell: ({ row }) => row.original.feature_module ?? '—',
  },
  {
    accessorKey: 'calls',
    header: 'Aufrufe',
    cell: ({ row }) => row.original.calls.toLocaleString('de-DE'),
    meta: { align: 'right' },
  },
  {
    accessorKey: 'cost_eur',
    header: 'Kosten (€)',
    cell: ({ row }) => fmtEur(row.original.cost_eur),
    meta: { align: 'right' },
  },
]

function StatisticsSection() {
  const [period, setPeriod] = useState<PeriodKey>('last-30-days')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [showComparison, setShowComparison] = useState(false)
  const [compPeriod, setCompPeriod] = useState<PeriodKey>('last-month')
  const [granularity, setGranularity] = useState<'day' | 'week' | 'month'>('day')
  const [tenantId, setTenantId] = useState<string>('')
  const [tenants, setTenants] = useState<Tenant[]>([])
  const [stats, setStats] = useState<ApiAiStatistics | null>(null)
  const [loading, setLoading] = useState(false)
  const [statsError, setStatsError] = useState<string | null>(null)

  useEffect(() => {
    getTenants()
      .then(({ data }) => setTenants(data))
      .catch(() => { /* ignore */ })
  }, [])

  const loadStats = useCallback(async () => {
    setLoading(true)
    setStatsError(null)
    try {
      const range = period === 'custom'
        ? { from: customFrom, to: customTo }
        : getDateRange(period)
      // Bug fix: backend uses dateFrom/dateTo (not from/to)
      const params: Record<string, string> = { dateFrom: range.from, dateTo: range.to, granularity }
      if (tenantId) params.tenantId = tenantId
      // Bug fix: correct endpoint is /ai/usage-stats (not /ai/statistics)
      const { data } = await apiClient.get<ApiAiStatistics>('/ai/usage-stats', { params })
      setStats(data)
    } catch (err: unknown) {
      const ax = err as { response?: { status?: number } }
      const status = ax?.response?.status
      if (status === 401 || status === 403) {
        setStatsError('Keine Berechtigung für Statistik-Daten.')
      } else {
        setStatsError('Statistik-Daten konnten nicht geladen werden. Bitte erneut versuchen.')
      }
    } finally {
      setLoading(false)
    }
  }, [period, customFrom, customTo, tenantId, granularity])

  useEffect(() => {
    void loadStats()
  }, [loadStats])

  const tenantOptions = [
    { value: '', label: 'Alle Mandanten' },
    ...tenants.map((t) => ({ value: t.id, label: t.name })),
  ]

  const periodOptions = (Object.entries(PERIOD_LABELS) as [PeriodKey, string][]).map(([k, v]) => ({
    value: k,
    label: v,
  }))

  const granularityOptions = [
    { value: 'day' as const, label: 'Täglich' },
    { value: 'week' as const, label: 'Wöchentlich' },
    { value: 'month' as const, label: 'Monatlich' },
  ]

  const range = period === 'custom'
    ? { from: customFrom, to: customTo }
    : getDateRange(period)

  const firstDate = stats?.firstLogDate
  const showHintBanner = Boolean(firstDate && range.from < firstDate)

  const summary = stats?.summary ?? {
    total_calls: 0,
    total_input_tokens: 0,
    total_output_tokens: 0,
    total_cost_eur: 0,
  }

  // Resolve tenant_id → name for the tenant breakdown table
  const tenantNameById = Object.fromEntries(tenants.map((t) => [t.id, t.name]))

  // Build tenant table rows with resolved names
  const tenantTableColumns: ColumnDef<ApiTenantRow, unknown>[] = [
    {
      id: 'tenantName',
      header: 'Mandant',
      cell: ({ row }) => {
        const tid = row.original.tenant_id
        return tid ? (tenantNameById[tid] ?? tid) : '—'
      },
    },
    {
      accessorKey: 'calls',
      header: 'Aufrufe',
      cell: ({ row }) => row.original.calls.toLocaleString('de-DE'),
      meta: { align: 'right' },
    },
    {
      accessorKey: 'cost_eur',
      header: 'Kosten (€)',
      cell: ({ row }) => fmtEur(row.original.cost_eur),
      meta: { align: 'right' },
    },
  ]

  return (
    <div className="space-y-6">
      {/* Filter bar */}
      <FilterBar>
        <Select<PeriodKey>
          value={period}
          onValueChange={setPeriod}
          options={periodOptions}
          wrapperClassName="min-w-[180px]"
        />

        <Select<'day' | 'week' | 'month'>
          value={granularity}
          onValueChange={setGranularity}
          options={granularityOptions}
          wrapperClassName="min-w-[140px]"
        />

        {period === 'custom' && (
          <>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-2 py-1.5 text-small focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              aria-label="Von"
            />
            <span className="text-caption text-gray-500">–</span>
            <input
              type="date"
              className="border border-gray-300 rounded-md px-2 py-1.5 text-small focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/30"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              aria-label="Bis"
            />
          </>
        )}

        <div className="flex items-center gap-1.5">
          <Switch
            checked={showComparison}
            onChange={(e) => setShowComparison(e.target.checked)}
            label="Vergleich"
          />
        </div>

        {showComparison && (
          <Select<PeriodKey>
            value={compPeriod}
            onValueChange={setCompPeriod}
            options={periodOptions.filter((o) => o.value !== 'custom')}
            wrapperClassName="min-w-[160px]"
          />
        )}

        <div className="ml-auto flex items-center gap-2">
          <Select
            value={tenantId}
            onValueChange={setTenantId}
            options={tenantOptions}
            wrapperClassName="min-w-[160px]"
          />
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            onClick={() => void loadStats()}
            disabled={loading}
          >
            Aktualisieren
          </Button>
        </div>
      </FilterBar>

      {/* Hinweis-Banner */}
      {showHintBanner && firstDate && (
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-caption text-blue-800">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Hinweis: Statistikdaten werden erst seit{' '}
            <strong>{new Date(firstDate).toLocaleDateString('de-DE')}</strong> erfasst.
            Früherer Zeitraum zeigt keine Daten.
          </span>
        </div>
      )}

      {/* Error state */}
      {statsError && !loading && (
        <ErrorState
          title="Statistik nicht verfügbar"
          description={statsError}
          onRetry={() => void loadStats()}
        />
      )}

      {/* Loading */}
      {loading && <LoadingState label="Statistikdaten werden geladen …" />}

      {/* Content */}
      {!loading && !statsError && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            <KpiCard
              label="Input Tokens gesamt"
              value={fmtTokens(Number(summary.total_input_tokens))}
            />
            <KpiCard
              label="Output Tokens gesamt"
              value={fmtTokens(Number(summary.total_output_tokens))}
            />
            <KpiCard
              label="Gesamt-Tokens"
              value={fmtTokens(Number(summary.total_input_tokens) + Number(summary.total_output_tokens))}
            />
            <KpiCard
              label="Aufrufe gesamt"
              value={Number(summary.total_calls ?? 0).toLocaleString('de-DE')}
            />
            <KpiCard
              label="Geschätzte Kosten"
              value={fmtEur(Number(summary.total_cost_eur))}
              sub="inkl. Wechselkurs"
            />
          </div>

          {/* Time series chart — backend field: period, input_tokens, output_tokens */}
          <Card>
            <Card.Header>
              <h3 className="text-body font-semibold text-gray-900 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-gray-400" />
                Token-Verlauf
              </h3>
            </Card.Header>
            <Card.Body>
              {stats && stats.timeSeries.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={stats.timeSeries} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                    <XAxis
                      dataKey="period"
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={(v: string) => v.slice(5)}
                    />
                    <YAxis
                      tick={{ fontSize: 10, fill: '#9ca3af' }}
                      axisLine={false}
                      tickLine={false}
                      tickFormatter={fmtTokens}
                    />
                    <RechartsTooltip
                      cursor={{ stroke: 'rgba(0,0,0,0.06)', strokeWidth: 1 }}
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null
                        return (
                          <div className="bg-white border border-gray-200 rounded shadow-sm px-3 py-2 text-xs space-y-1">
                            <div className="font-medium text-gray-700">{label}</div>
                            {payload.map((p) => (
                              <div key={p.dataKey as string} className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
                                <span className="text-gray-600">{p.name}: {fmtTokens(p.value as number)}</span>
                              </div>
                            ))}
                          </div>
                        )
                      }}
                    />
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                    />
                    <Line type="monotone" dataKey="input_tokens" name="Input" stroke="#6366f1" dot={false} strokeWidth={2} />
                    <Line type="monotone" dataKey="output_tokens" name="Output" stroke="#22c55e" dot={false} strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  size="sm"
                  title="Keine Zeitreihendaten"
                  description="Für den gewählten Zeitraum liegen keine Token-Logs vor."
                  icon={<BarChart3 />}
                />
              )}
            </Card.Body>
          </Card>

          {/* Breakdown tables */}
          <div className="space-y-4">
            <h3 className="text-body font-semibold text-gray-900">Aufschlüsselung</h3>

            {/* By provider × model */}
            <Card>
              <Card.Header>
                <h4 className="text-small font-semibold text-gray-800">Provider × Modell</h4>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable<ApiModelRow>
                  columns={MODEL_COLUMNS}
                  data={stats?.byProviderModel ?? []}
                  enableSorting
                  searchable={false}
                  emptyState={
                    <EmptyState size="sm" title="Keine Daten" description="Für diesen Zeitraum keine Modell-Logs." />
                  }
                />
              </Card.Body>
            </Card>

            {/* By feature module */}
            <Card>
              <Card.Header>
                <h4 className="text-small font-semibold text-gray-800">Feature-Modul</h4>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable<ApiModuleRow>
                  columns={MODULE_COLUMNS}
                  data={stats?.byFeatureModule ?? []}
                  enableSorting
                  searchable={false}
                  emptyState={
                    <EmptyState size="sm" title="Keine Daten" description="Für diesen Zeitraum keine Modul-Logs." />
                  }
                />
              </Card.Body>
            </Card>

            {/* By tenant */}
            <Card>
              <Card.Header>
                <h4 className="text-small font-semibold text-gray-800">Mandant</h4>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable<ApiTenantRow>
                  columns={tenantTableColumns}
                  data={stats?.byTenant ?? []}
                  enableSorting
                  searchable={false}
                  emptyState={
                    <EmptyState size="sm" title="Keine Daten" description="Für diesen Zeitraum keine Mandanten-Logs." />
                  }
                />
              </Card.Body>
            </Card>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Main Export ─────────────────────────────────────────────────────────────

export function KiStatistikTab() {
  return (
    <div className="space-y-8">
      <PricingSection />
      <StatisticsSection />
    </div>
  )
}
